import { useAuthContext } from "providers/Auth";
import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getShortAddressFromAddress } from "shared/addressUtils";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useMedia } from "shared/providers";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Clock,
  Database01,
  Plus,
  RefreshCCW02,
  Trash04
} from "untitledui-js-base";
import { PropertyDetailItem } from "./PropertyDetailItem";
import { Settings04 } from "untitledui-js-base";
import { Property, Unit } from "types/landlord/address";
import { PartnerFeatures, PropertyKind } from "shared/types";

import { Loader } from "shared/components/Loader";
import moment from "moment";
import {
  ContextMenuPopover,
  type Action
} from "shared/components/ContextMenuPopover";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Calendar03Icon,
  PlazaIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { DeletePropertyModal } from "shared/app/DeletePropertyModal";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { getSourceLabel } from "utils/getSourceLabel";
import { getPropertyKindLabel } from "shared/utils/property-kind-label";
import { cx } from "shared/utils";
import { TemplateLinksSubmenu } from "shared/components/TemplateLinksSubmenu/TemplateLinksSubmenu";
import { useEligibleContactFormSources } from "shared/components/ContactFormsSubmenu/useEligibleContactFormSources";
import { ContactFormsSubmenu } from "shared/components/ContactFormsSubmenu";

interface LayoutHeaderProps {
  propertyName: string;
  property: Property;
  setShowInviteApplicantsModal: (show: boolean) => void;
  isSingleSyncSupported: boolean;
  isSyncing: boolean;
  handleRequestSync: () => void;
  lastSuccessSyncedAt?: string;
  setShowActivityLog: (show: boolean) => void;
}

const getDeleteTooltip = (
  isBoomscreenAndPms: boolean,
  canDelete: boolean,
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>
) => {
  if (isBoomscreenAndPms) return `You can't delete a ${dictionary.property}`;

  if (!canDelete)
    return `You can't delete a ${dictionary.property} with applications`;

  return null;
};

interface CopyLinksMenuProps {
  property: Property;
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>;
  /** Partner-level `showings` entitlement; hides the request-showing link entirely. */
  isShowingsEnabled: boolean;
  isRequestShowingEnabled: boolean;
  getRequestShowingTooltip: () => string | undefined;
  handleCopyClick: (link: string) => void;
  hasEligibleContactFormSources: boolean;
}

const IconTitle = ({
  icon,
  label
}: {
  icon: React.ComponentProps<typeof HugeiconsIcon>["icon"];
  label: string;
}) => (
  <div className="flex gap-3 items-center py-1">
    <HugeiconsIcon
      icon={icon}
      size={20}
      stroke="1px"
      className="text-content-200"
    />
    <p className="text-base text-content-1000 font-medium">{label}</p>
  </div>
);

const resolveListingPageLabel = ({ propertyWord }: { propertyWord: string }) =>
  `Copy ${propertyWord} listings page`;

const buildUnitListingPageAction = ({
  firstUnit,
  dictionary,
  handleCopyClick
}: {
  firstUnit: Unit | undefined;
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>;
  handleCopyClick: (link: string) => void;
}): Action => ({
  title: (
    <IconTitle
      icon={PlazaIcon}
      label={`Copy ${dictionary.property} listing page`}
    />
  ),
  disabled: !firstUnit?.listed,
  tooltip: !firstUnit?.listed
    ? "You can't copy a link to a unit without listing it"
    : undefined,
  onClick: () => {
    handleCopyClick(firstUnit?.listing_url || "");
  }
});

const buildPortfolioListingPageAction = ({
  property,
  dictionary,
  handleCopyClick
}: {
  property: Property;
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>;
  handleCopyClick: (link: string) => void;
}): Action => ({
  title: (
    <IconTitle
      icon={PlazaIcon}
      label={resolveListingPageLabel({ propertyWord: dictionary.property })}
    />
  ),
  disabled: !property.listing_url,
  onClick: () => {
    handleCopyClick(property.listing_url || "");
  }
});

const buildPortfolioPageAction = ({
  property,
  dictionary,
  handleCopyClick
}: {
  property: Property;
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>;
  handleCopyClick: (link: string) => void;
}): Action => {
  const portfolioPageUrl = property.magic_link?.application_link || "";
  return {
    title: (
      <IconTitle
        icon={PlazaIcon}
        label={`Copy ${dictionary.property} portfolio page`}
      />
    ),
    disabled: !portfolioPageUrl,
    onClick: () => {
      handleCopyClick(portfolioPageUrl);
    }
  };
};

const buildTemplateLinksAction = ({
  firstUnit,
  handleCopyClick
}: {
  firstUnit: Unit | undefined;
  handleCopyClick: (link: string) => void;
}): Action => ({
  title: (
    <TemplateLinksSubmenu
      variant="FULL"
      disabled={!firstUnit?.magic_link?.active}
      onCopyLink={handleCopyClick}
      defaultLink={firstUnit?.magic_link?.application_link || ""}
      unitId={firstUnit?.id ? String(firstUnit.id) : undefined}
    />
  ),
  disabled: !firstUnit?.magic_link?.active,
  tooltip: !firstUnit?.magic_link?.active
    ? "When a unit is not accepting applications, applicants can only be invited via email"
    : undefined,
  onClick: (e: React.MouseEvent) => {
    e.stopPropagation();
  }
});

// No has_available_unit gate: CRM-982 §9 keeps a property with unlisted/occupied units
// (or none at all) reachable through the contact form, so its link must stay copyable.
const buildContactFormAction = ({
  property
}: {
  property: Property;
}): Action => ({
  title: <ContactFormsSubmenu variant="FULL" propertyId={property.id} />,
  onClick: (e: React.MouseEvent) => {
    e.stopPropagation();
  }
});

const buildRequestShowingAction = ({
  firstUnit,
  isRequestShowingEnabled,
  getRequestShowingTooltip,
  handleCopyClick
}: {
  firstUnit: Unit | undefined;
  isRequestShowingEnabled: boolean;
  getRequestShowingTooltip: () => string | undefined;
  handleCopyClick: (link: string) => void;
}): Action => ({
  title: <IconTitle icon={Calendar03Icon} label="Copy request showing link" />,
  onClick: () => {
    handleCopyClick(
      firstUnit?.listing_url ? `${firstUnit.listing_url}/request-showing` : ""
    );
  },
  disabled: !firstUnit?.listed || !isRequestShowingEnabled,
  tooltip: getRequestShowingTooltip()
});

const CopyLinksMenu = ({
  property,
  dictionary,
  isShowingsEnabled,
  isRequestShowingEnabled,
  getRequestShowingTooltip,
  handleCopyClick,
  hasEligibleContactFormSources
}: CopyLinksMenuProps) => {
  const firstUnit = property.first_unit ?? property.units?.[0];
  const isSingleFamily = property.kind === PropertyKind.SingleFamily;
  const showUnitListingActions =
    isSingleFamily && Boolean(firstUnit?.listing_url);

  const listingPageActions = showUnitListingActions
    ? [buildUnitListingPageAction({ firstUnit, dictionary, handleCopyClick })]
    : [
        buildPortfolioListingPageAction({
          property,
          dictionary,
          handleCopyClick
        }),
        buildPortfolioPageAction({ property, dictionary, handleCopyClick })
      ];

  const actions = [
    ...listingPageActions,
    ...(showUnitListingActions
      ? [buildTemplateLinksAction({ firstUnit, handleCopyClick })]
      : []),
    ...(hasEligibleContactFormSources
      ? [buildContactFormAction({ property })]
      : []),
    ...(showUnitListingActions && isShowingsEnabled
      ? [
          buildRequestShowingAction({
            firstUnit,
            isRequestShowingEnabled,
            getRequestShowingTooltip,
            handleCopyClick
          })
        ]
      : [])
  ];

  return (
    <ContextMenuPopover
      placement="bottom-end"
      cbTrigger={(open) => (
        <Button
          HugeIcon={open ? ArrowUp01Icon : ArrowDown01Icon}
          size={ButtonSize.SM}
          iconPosition="right"
        >
          Copy link
        </Button>
      )}
      actions={actions}
    />
  );
};

export const LayoutHeader = memo(
  ({
    propertyName,
    property,
    setShowInviteApplicantsModal,
    isSingleSyncSupported,
    isSyncing,
    handleRequestSync,
    lastSuccessSyncedAt,
    setShowActivityLog
  }: LayoutHeaderProps) => {
    const { isMobile } = useMedia();
    const navigate = useNavigate();
    const dictionary = useUnitsPropertiesDictionary();
    const { type, partnerFeatures } = useAuthContext();
    const propertyKindLabel = getPropertyKindLabel(property.kind);
    const { eligibleSources } = useEligibleContactFormSources();
    const hasEligibleContactFormSources = eligibleSources.length > 0;
    const [showDetails, setShowDetails] = useState(false);
    const [showDeletePropertyModal, setShowDeletePropertyModal] =
      useState(false);
    const hasPropertyOwner = partnerFeatures.includes(
      PartnerFeatures.PropertyOwner
    );

    const isRequestShowingEnabled = Boolean(
      property.prescreening_template?.show_request_showing_button
    );
    const getRequestShowingTooltip = () => {
      const firstUnit = property.first_unit ?? property.units?.[0];
      if (!firstUnit?.listed)
        return "You can't copy a link to a unit without listing it";
      if (!isRequestShowingEnabled)
        return "Request showing is not enabled for this listing";
      return undefined;
    };

    const handleCopyClick = async (link: string) => {
      try {
        await navigator.clipboard.writeText(link);
        toast.info("Link copied");
      } catch (err) {
        toast.error(getErrorMessage(err));
      }
    };

    const isBoomscreenAndPms = type.isPMS && type.isBoomScreen;

    const isDeleteDisabled = !property.can_delete || isBoomscreenAndPms;
    return (
      <>
        <div className="flex items-center justify-between border-b border-alpha-40 py-2 pl-3 md:px-6 md:py-3 gap-2 flex-wrap md:flex-nowrap">
          {isMobile ? (
            <Button
              borderLess
              size={ButtonSize.SM}
              Icon={ArrowLeft}
              onClick={() => {
                navigate(-1);
              }}
            >
              Back
            </Button>
          ) : (
            <BreadCrumbs
              crumbs={[
                {
                  title: dictionary.Properties,
                  href: "/properties"
                },
                {
                  title: propertyName || `Current ${dictionary.property}`,
                  href: `/properties/${property.id}`
                }
              ]}
            />
          )}
          <div className="flex items-center gap-2">
            {isSingleSyncSupported && (
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-content-200 hidden md:block">
                  Last sync:{" "}
                  {lastSuccessSyncedAt
                    ? moment(lastSuccessSyncedAt).format("MMM DD, YYYY, h:mm A")
                    : "N/A"}
                </p>
                {isSyncing ? (
                  <Loader size={32} />
                ) : (
                  <IconButton
                    className="shrink-0 bg-transparent hover:bg-alpha-40"
                    borderLess
                    Icon={RefreshCCW02}
                    onClick={handleRequestSync}
                    size={IconButtonSize.SM}
                  />
                )}
              </div>
            )}
            <Button
              borderLess
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              className="!text-content-400 !stroke-content-400"
              Icon={Clock}
              onClick={() => setShowActivityLog(true)}
            >
              Activity log
            </Button>
            <Button
              borderLess
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              className={cx(
                "stroke-content-400 text-content-400 hover:bg-alpha-40",
                isDeleteDisabled ? "!text-alpha-100" : ""
              )}
              onClick={() => setShowDeletePropertyModal(true)}
              Icon={Trash04}
              disabled={isDeleteDisabled}
              tooltip={getDeleteTooltip(
                isBoomscreenAndPms,
                property.can_delete,
                dictionary
              )}
            >
              <p className="hidden md:flex">Delete {dictionary.Property}</p>
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between border-b border-alpha-40 px-6 py-5 gap-2 flex-wrap md:flex-nowrap">
          <div className="flex flex-col gap-1">
            {propertyKindLabel ? (
              <Badge
                variant={BadgeVariant.PRIMARY}
                size={BadgeSize.SM}
                className="w-fit"
              >
                {propertyKindLabel}
              </Badge>
            ) : null}
            <h4 className="shrink-0 text-xl font-bold text-content-1000 break-all max-w-[calc(80vw-200px)]">
              {propertyName || `Current ${dictionary.property}`}
            </h4>
          </div>
          <Show if={isMobile}>
            <p className="text-content-500 text-xs font-normal">
              {getShortAddressFromAddress(property.address)}
            </p>
          </Show>
          <div className="flex gap-2 items-center">
            <Show if={type.isBoomCRM}>
              <CopyLinksMenu
                property={property}
                dictionary={dictionary}
                isShowingsEnabled={type.isShowings}
                isRequestShowingEnabled={isRequestShowingEnabled}
                getRequestShowingTooltip={getRequestShowingTooltip}
                handleCopyClick={handleCopyClick}
                hasEligibleContactFormSources={hasEligibleContactFormSources}
              />
            </Show>

            <Hide if={type.isBoomCRM}>
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.BASE}
                onClick={() => {
                  handleCopyClick(property.magic_link?.application_link || "");
                }}
                className="shrink-0"
              >
                Copy portfolio link
              </Button>
            </Hide>

            {type.isBoomScreen ? (
              <div className="hidden md:flex w-full ml-auto">
                <Button
                  onClick={() => setShowInviteApplicantsModal(true)}
                  variant={ButtonVariant.PRIMARY}
                  className="ml-auto md:w-auto w-full"
                  size={ButtonSize.SM}
                  Icon={Plus}
                >
                  Invite applicants
                </Button>
              </div>
            ) : null}
          </div>
        </div>
        <div className="border-b border-alpha-100 flex md:items-center justify-start flex-wrap md:flex-nowrap gap-2 md:gap-12 md:px-6 md:py-4 pb-2">
          <Show if={type.isBoomScreen}>
            {isMobile ? (
              <div className="pl-3 py-2 w-full">
                <Show if={!showDetails}>
                  <Button
                    borderLess
                    size={ButtonSize.SM}
                    Icon={ChevronDown}
                    onClick={() => setShowDetails(true)}
                  >
                    Show details
                  </Button>
                </Show>

                <Show if={showDetails}>
                  <div className="flex flex-col pt-4 gap-3 px-4 w-full">
                    <PropertyDetailItem
                      label="Screen settings"
                      value={property.verification_template?.name ?? "--"}
                      icon={
                        <Settings04
                          className="shrink-0 stroke-content-500"
                          size="20"
                        />
                      }
                    />

                    <Show if={type.isPMS}>
                      <PropertyDetailItem
                        label="Source"
                        value={getSourceLabel(property)}
                        icon={
                          <Database01
                            className="shrink-0 stroke-content-500"
                            size="20"
                          />
                        }
                      />
                    </Show>

                    <Show if={hasPropertyOwner}>
                      <PropertyDetailItem
                        label="Owner"
                        valueClassName="truncate max-w-[100px]"
                        value={property.owner || "--"}
                      />
                    </Show>

                    <PropertyDetailItem
                      label="Address"
                      value={getShortAddressFromAddress(property.address)}
                    />

                    <PropertyDetailItem
                      label={dictionary.Property + " ID"}
                      value={property.external_id || "--"}
                    />

                    <PropertyDetailItem
                      label="Property group"
                      value={property.property_group?.name || "--"}
                    />
                  </div>
                </Show>

                <Show if={showDetails}>
                  <Button
                    borderLess
                    size={ButtonSize.SM}
                    Icon={ChevronUp}
                    className="mt-2"
                    onClick={() => setShowDetails(false)}
                  >
                    Hide details
                  </Button>
                </Show>
              </div>
            ) : (
              <div className="flex gap-12">
                <PropertyDetailItem
                  label="Screen settings"
                  value={property.verification_template?.name ?? "--"}
                  labelClassName="text-content-1000"
                />
                <PropertyDetailItem
                  label="Address"
                  value={getShortAddressFromAddress(property.address)}
                  valueClassName="break-all max-w-[600px]"
                />
                <Show if={type.isPMS}>
                  <PropertyDetailItem
                    label="Source"
                    value={getSourceLabel(property)}
                  />
                </Show>
                <Show if={hasPropertyOwner}>
                  <PropertyDetailItem
                    label="Owner"
                    valueClassName="truncate max-w-[100px]"
                    value={property.owner || "--"}
                  />
                </Show>
                <PropertyDetailItem
                  label={dictionary.Property + " ID"}
                  value={property.external_id || "--"}
                />
                <PropertyDetailItem
                  label="Property group"
                  value={
                    <p className="text-content-1000 font-medium">
                      {property.property_group?.name || "--"}
                    </p>
                  }
                />
              </div>
            )}
          </Show>
        </div>

        <DeletePropertyModal
          opened={showDeletePropertyModal}
          onClose={() => setShowDeletePropertyModal(false)}
          propertyId={property.id.toString()}
          onSuccess={() => {
            navigate("/properties");
          }}
        />
      </>
    );
  }
);

LayoutHeader.displayName = "LayoutHeader";
